import { Component, inject, OnInit, signal } from '@angular/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { WorkPermissionsEnum, WorkUsersV2Service, WorkUserV2 } from '@work-frontend-monorepo/work-rest-api';
import { AvatarComponent, ButtonComponent, IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { FormatDatePipe, PrependServerToFileSrcPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { isSwissMobilePhoneNumber } from '@work-frontend-monorepo/work-ui/utilities';
import { Store } from '@ngxs/store';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'sfg-app-mitarbeitende-adressbuch-details-page',
  templateUrl: './adressbuch-mitarbeitende-details-page.component.html',
  styleUrls: ['./adressbuch-mitarbeitende-details-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    ReactiveFormsModule,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonMenuButton,
    IonButtons,
    TranslatePipe,
    PrependServerToFileSrcPipe,
    AvatarComponent,
    IconComponent,
    IonBackButton,
    FormatDatePipe,
    ButtonComponent,
    AsyncPipe,
  ],
})
export class AdressbuchMitarbeitendeDetailsPageComponent implements OnInit {
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workUsersV2Service = inject(WorkUsersV2Service);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);

  user = signal<WorkUserV2 | undefined>(undefined);
  canReadPersonnelDossier$ = this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.PersonnelFileADMIN));

  async ngOnInit() {
    const userId = this.activatedRoute.snapshot.params['userId'];
    if (userId) {
      await this.loadUserDetails(userId);
    }
  }

  private async loadUserDetails(userId: number) {
    try {
      const userResponse = await lastValueFrom(
        this.workUsersV2Service.usersV2ControllerFindOneById({
          id: userId,
        }),
      );
      this.user.set(userResponse);
    } catch (loadUserDetailsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadUserDetailsException', loadUserDetailsException);
    }
  }

  protected call(phoneNumber: string) {
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    window.open(`tel:${cleanNumber}`, '_system');
  }

  protected sendSMS(phoneNumber: string) {
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    window.open(`sms:${cleanNumber}`, '_system');
  }

  protected openWhatsApp(phoneNumber: string) {
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_system');
  }

  protected createMail(email: string) {
    window.open(`mailto:${email}`, '_system');
  }

  protected openGoogleMaps() {
    const user = this.user();
    if (user?.street && user?.city) {
      const address = encodeURIComponent(`${user.street}, ${user.city}`);
      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_system');
    }
  }

  protected readonly isSwissMobilePhoneNumber = isSwissMobilePhoneNumber;
}
